import DateConverter from './DateConverter';

interface CalendarState {
  currentYear: number;
  currentMonth: number;
  selectedDate: { year: number; month: number; day: number } | null;
}

class DatePicker {
  private readonly converter: DateConverter;
  public input: HTMLInputElement | null;
  public onDateChange: ((date: string) => void) | null;
  private calendar: HTMLDivElement | null;
  private state: CalendarState;

  constructor(inputId: string) {
    this.converter = new DateConverter();
    this.input = document.getElementById(inputId) as HTMLInputElement;
    this.onDateChange = null;
    this.calendar = null;

    // Initialize with current Ethiopian date
    const today = new Date();
    const ethiopianDate = this.converter.convertGregorianToEthiopian(this.formatDateToString(today)).ethiopianDate;
    this.state = {
      currentYear: ethiopianDate.year,
      currentMonth: ethiopianDate.month,
      selectedDate: null
    };

    if (this.input) {
      this.input.addEventListener('focus', this.showDatePicker.bind(this));
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  private isLeapYear(year: number): boolean {
    // Ethiopian leap year calculation
    // A year is a leap year if the year divided by 4 gives remainder 3
    return year % 4 === 3;
  }

  private getDaysInMonth(year: number, month: number): number {
    if (month === 13) {
      // ጷጉሜን has 6 days in leap years, 5 days otherwise
      return this.isLeapYear(year) ? 6 : 5;
    }
    return 30; // All other months have 30 days
  }

  private validateDate(year: number, month: number, day: number): boolean {
    if (month < 1 || month > 13) return false;
    const daysInMonth = this.getDaysInMonth(year, month);
    return day >= 1 && day <= daysInMonth;
  }

  private formatDateToString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private handleClickOutside(event: MouseEvent): void {
    if (this.calendar && event.target instanceof Node) {
      if (!this.calendar.contains(event.target) && event.target !== this.input) {
        this.calendar.remove();
        this.calendar = null;
      }
    }
  }

  private createNavigationHeader(): HTMLDivElement {
    const header = document.createElement('div');
    header.className = 'picker-header';

    const prevButton = document.createElement('button');
    prevButton.textContent = '←';
    prevButton.onclick = (e) => {
      e.stopPropagation(); // Prevent event from bubbling up
      this.navigateMonth(-1);
    };

    const nextButton = document.createElement('button');
    nextButton.textContent = '→';
    nextButton.onclick = (e) => {
      e.stopPropagation(); // Prevent event from bubbling up
      this.navigateMonth(1);
    };

    // Create month select
    const monthSelect = document.createElement('select');
    monthSelect.className = 'month-select';
    monthSelect.onclick = (e) => e.stopPropagation(); // Prevent closing when clicking the select
    this.converter.ethiopianMonths.forEach((month, index) => {
      const option = document.createElement('option');
      option.value = String(index + 1);
      option.textContent = month;
      option.selected = index + 1 === this.state.currentMonth;
      monthSelect.appendChild(option);
    });
    monthSelect.onchange = (e) => {
      e.stopPropagation(); // Prevent event from bubbling up
      const target = e.target as HTMLSelectElement;
      const newMonth = parseInt(target.value);
      
      // If there's a selected date, validate it with the new month
      if (this.state.selectedDate) {
        const daysInNewMonth = this.getDaysInMonth(this.state.currentYear, newMonth);
        if (this.state.selectedDate.day > daysInNewMonth) {
          // Adjust the selected date to the last day of the new month
          this.state.selectedDate.day = daysInNewMonth;
        }
      }
      
      this.state.currentMonth = newMonth;
      this.updateCalendar();
    };

    // Create year select
    const yearSelect = document.createElement('select');
    yearSelect.className = 'year-select';
    yearSelect.onclick = (e) => e.stopPropagation(); // Prevent closing when clicking the select
    const currentYear = this.state.currentYear;
    const startYear = currentYear - 20;
    const endYear = currentYear + 20;
    for (let year = startYear; year <= endYear; year++) {
      const option = document.createElement('option');
      option.value = String(year);
      option.textContent = String(year);
      option.selected = year === currentYear;
      yearSelect.appendChild(option);
    }
    yearSelect.onchange = (e) => {
      e.stopPropagation(); // Prevent event from bubbling up
      const target = e.target as HTMLSelectElement;
      const newYear = parseInt(target.value);
      
      // If there's a selected date and it's in the 13th month, validate it
      if (this.state.selectedDate && this.state.selectedDate.month === 13) {
        const daysInNewMonth = this.getDaysInMonth(newYear, 13);
        if (this.state.selectedDate.day > daysInNewMonth) {
          // Adjust the selected date to the last day of ጷጉሜን
          this.state.selectedDate.day = daysInNewMonth;
        }
      }
      
      this.state.currentYear = newYear;
      this.updateCalendar();
    };

    // Create a container for the selects
    const selectContainer = document.createElement('div');
    selectContainer.className = 'select-container';
    selectContainer.style.display = 'flex';
    selectContainer.style.gap = '8px';
    selectContainer.style.alignItems = 'center';
    selectContainer.onclick = (e) => e.stopPropagation(); // Prevent closing when clicking the container
    selectContainer.appendChild(monthSelect);
    selectContainer.appendChild(yearSelect);

    header.appendChild(prevButton);
    header.appendChild(selectContainer);
    header.appendChild(nextButton);

    return header;
  }

  private navigateMonth(delta: number): void {
    let newMonth = this.state.currentMonth + delta;
    let newYear = this.state.currentYear;

    if (newMonth > 13) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 13;
      newYear--;
    }

    // Validate selected date when navigating months
    if (this.state.selectedDate) {
      const daysInNewMonth = this.getDaysInMonth(newYear, newMonth);
      if (this.state.selectedDate.day > daysInNewMonth) {
        this.state.selectedDate.day = daysInNewMonth;
      }
    }

    this.state.currentMonth = newMonth;
    this.state.currentYear = newYear;
    this.updateCalendar();
  }

  private createDaysGrid(): HTMLTableElement {
    const table = document.createElement('table');
    table.className = 'calendar-grid';
    table.style.width = '100%';

    // Create table header with day names
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    const dayNames = ['ሰኞ', 'ማክሰኞ', 'ረቡዕ', 'ሐሙስ', 'ዓርብ', 'ቅዳሜ', 'እሑድ'];
    dayNames.forEach(day => {
      const th = document.createElement('th');
      th.textContent = day;
      th.style.width = '14.28%';
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Create table body with day cells
    const tbody = document.createElement('tbody');
    const daysInMonth = this.getDaysInMonth(this.state.currentYear, this.state.currentMonth);
    let currentRow = document.createElement('tr');

    // Get current Ethiopian date for highlighting
    const today = new Date();
    const currentEthiopianDate = this.converter.convertGregorianToEthiopian(
      this.formatDateToString(today)
    ).ethiopianDate;

    // Add empty cells for proper day alignment
    const firstDayOfMonth = this.getFirstDayOfMonth();
    for (let i = 0; i < firstDayOfMonth; i++) {
      const emptyCell = document.createElement('td');
      currentRow.appendChild(emptyCell);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      if ((day + firstDayOfMonth - 1) % 7 === 0 && day !== 1) {
        tbody.appendChild(currentRow);
        currentRow = document.createElement('tr');
      }

      const td = document.createElement('td');
      td.textContent = String(day);
      td.className = 'day';

      // Add current-day class if it's today
      if (
        day === currentEthiopianDate.day &&
        this.state.currentMonth === currentEthiopianDate.month &&
        this.state.currentYear === currentEthiopianDate.year
      ) {
        td.classList.add('current-day');
      }

      // Add selected-day class if this date is selected
      if (
        day === this.state.selectedDate?.day &&
        this.state.currentMonth === this.state.selectedDate?.month &&
        this.state.currentYear === this.state.selectedDate?.year
      ) {
        td.classList.add('selected-day');
      }

      td.onclick = () => this.selectDate(day);
      currentRow.appendChild(td);
    }

    tbody.appendChild(currentRow);
    table.appendChild(tbody);
    return table;
  }

  private getFirstDayOfMonth(): number {
    // This is a simplified version. For accurate implementation,
    // you would need to calculate the actual first day of the Ethiopian month
    return 0; // Assuming months start on Monday for now
  }

  private selectDate(day: number): void {
    if (!this.validateDate(this.state.currentYear, this.state.currentMonth, day)) {
      return;
    }

    // Update selected date in state
    this.state.selectedDate = {
      year: this.state.currentYear,
      month: this.state.currentMonth,
      day: day
    };

    // Update the calendar to show the selection
    this.updateCalendar();

    if (this.input && this.onDateChange) {
      const formattedDate = `${this.state.selectedDate.day} ${this.converter.getMonthName(this.state.selectedDate.month)} ${this.state.selectedDate.year}`;
      this.input.value = formattedDate;
      this.onDateChange(formattedDate);
      this.calendar?.remove();
      this.calendar = null;
    }
  }

  private updateCalendar(): void {
    if (this.calendar) {
      this.calendar.innerHTML = '';
      this.calendar.appendChild(this.createNavigationHeader());
      this.calendar.appendChild(this.createDaysGrid());
    }
  }

  private showDatePicker(): void {
    if (this.calendar) {
      return;
    }

    this.calendar = document.createElement('div');
    this.calendar.className = 'ethiopian-date-picker';
    this.calendar.style.minWidth = '350px';
    
    this.updateCalendar();

    // Position the calendar below the input
    if (this.input) {
      const rect = this.input.getBoundingClientRect();
      this.calendar.style.position = 'absolute';
      this.calendar.style.top = `${rect.bottom + 5}px`;
      this.calendar.style.left = `${rect.left}px`;
      document.body.appendChild(this.calendar);
    }
  }

  public destroy(): void {
    if (this.input) {
      this.input.removeEventListener('focus', this.showDatePicker.bind(this));
      document.removeEventListener('click', this.handleClickOutside.bind(this));
    }
    if (this.calendar) {
      this.calendar.remove();
      this.calendar = null;
    }
  }
}

export default DatePicker;
